import { FC, useState } from "react";
import { Page } from "../../../Components/Page";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { useCompetition } from "../../../hooks/useCompetition";
import { useTeamList } from "../../../hooks/useTeamList";
import { useGameList } from "../../../hooks/useGameList";
import {
  Avatar,
  Badge,
  Button,
  Card,
  ColorSwatch,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { TeamName } from "../../../Components/TeamName";
import { ResolvedTeamReference } from "../../../Components/ResolvedTeamReference";
import { ResolveSlotsModal } from "./ResolveSlotsModal";
import { CreateGroupModal } from "./Modals/CreateGroupModal";
import { AddTeamToGroupModal } from "./Modals/AddTeamToGroupModal";
import { EnrollTeamsModal } from "./Modals/EnrollTeamsModal";
import { CreateMatchdayModal } from "./Modals/CreateMatchdayModal";
import { components } from "../../../openapi/generated-types";

type CompetitionGroup = components["schemas"]["CompetitionGroup"];

interface GroupCardProps {
  group: CompetitionGroup;
  enrolledTeamIds: string[];
  competitionId: string;
  onAddTeamClick: (groupId: string) => void;
  onResolveSlotsClick: (groupId: string) => void;
}

const GroupCard: FC<GroupCardProps> = ({
  group,
  enrolledTeamIds,
  competitionId,
  onAddTeamClick,
  onResolveSlotsClick,
}) => {
  const availableTeams = enrolledTeamIds.filter(
    (id) => !group.teamIds.includes(id),
  );

  const hasSlots = group.slots && group.slots.length > 0;
  const isStaticGroup = !hasSlots;
  const isDynamicResolved = hasSlots && group.teamIds.length > 0;

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="sm">
        <Title order={4}>{group.name}</Title>
        {isStaticGroup ? (
          <Badge color="gray" variant="light">
            Statisch
          </Badge>
        ) : (
          <Badge color={isDynamicResolved ? "green" : "orange"} variant="light">
            {isDynamicResolved ? "Aufgelöst" : "Dynamisch"}
          </Badge>
        )}
      </Group>

      {group.teamIds.length > 0 && (
        <Table mb="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Mannschaft</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {group.teamIds.map((teamId) => (
              <Table.Tr key={teamId}>
                <Table.Td>
                  <TeamName teamId={teamId} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {hasSlots && group.teamIds.length === 0 && (
        <Table mb="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w="1%">Slot</Table.Th>
              <Table.Th>Referenz</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {group.slots.map((slot, index) => (
              <Table.Tr key={index}>
                <Table.Td>{index + 1}</Table.Td>
                <Table.Td>
                  <ResolvedTeamReference
                    teamReference={slot}
                    competitionId={competitionId}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      {group.teamIds.length === 0 && !hasSlots && (
        <Text c="dimmed" mb="sm">
          Keine Mannschaften in dieser Gruppe
        </Text>
      )}

      <Group>
        {isStaticGroup && (
          <Button
            variant="light"
            onClick={() => {
              onAddTeamClick(group.id);
            }}
            disabled={availableTeams.length === 0}
          >
            Mannschaft hinzufügen
          </Button>
        )}

        {hasSlots && group.teamIds.length === 0 && (
          <Button
            variant="filled"
            onClick={() => {
              onResolveSlotsClick(group.id);
            }}
          >
            Slots auflösen
          </Button>
        )}
      </Group>
    </Card>
  );
};

export const Groups: FC = () => {
  const { competitionId } = useRequiredParams(["competitionId"]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [addTeamModalGroupId, setAddTeamModalGroupId] = useState<string | null>(
    null,
  );
  const [resolveSlotsGroupId, setResolveSlotsGroupId] = useState<string | null>(
    null,
  );
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [createMatchdayModalOpen, setCreateMatchdayModalOpen] = useState(false);

  const competition = useCompetition(competitionId);
  const teams = useTeamList();
  const games = useGameList(competitionId);

  if (!competition.isSuccess || !teams.isSuccess || !games.isSuccess) {
    return <Loader />;
  }

  const enrolledTeams = teams.data.teams.filter((t) =>
    competition.data.teamIds.includes(t.id),
  );

  const getGroupsForTeam = (teamId: string): string[] => {
    return competition.data.groups
      .filter((g) => g.teamIds.includes(teamId))
      .map((g) => g.name);
  };

  const getGameCountForTeam = (teamId: string): number => {
    return games.data.games.filter(
      (g) =>
        g.actualTeams.homeTeamId === teamId ||
        g.actualTeams.guestTeamId === teamId,
    ).length;
  };

  const getAvailableTeamsForGroup = (groupId: string) => {
    const group = competition.data.groups.find((g) => g.id === groupId);
    if (!group) return [];
    return enrolledTeams.filter((t) => !group.teamIds.includes(t.id));
  };

  const availableTeamsToEnroll = teams.data.teams.filter(
    (t) => !competition.data.teamIds.includes(t.id),
  );

  return (
    <Page title={`Mannschaften & Gruppen - ${competition.data.name}`}>
      <Stack gap="xl">
        {/* Section: Mannschaften im Turnier */}
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Title order={3}>Mannschaften im Turnier</Title>
            <Button
              onClick={() => {
                setEnrollModalOpen(true);
              }}
              disabled={availableTeamsToEnroll.length === 0}
            >
              Mannschaften hinzufügen
            </Button>
          </Group>
          {enrolledTeams.length === 0 ? (
            <Text c="dimmed">Keine Mannschaften eingetragen</Text>
          ) : (
            <Table.ScrollContainer minWidth={500}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w="1%">Logo</Table.Th>
                    <Table.Th w="1%">Farbe</Table.Th>
                    <Table.Th w="1%" style={{ whiteSpace: "nowrap" }}>
                      Mannschaft
                    </Table.Th>
                    <Table.Th w="1%">Spiele</Table.Th>
                    <Table.Th>Gruppen</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {enrolledTeams.map((team) => {
                    const groupNames = getGroupsForTeam(team.id);
                    const gameCount = getGameCountForTeam(team.id);
                    return (
                      <Table.Tr key={team.id}>
                        <Table.Td>
                          <Avatar
                            src={team.logoFilePath ?? undefined}
                            size="sm"
                            radius="xs"
                          >
                            {team.shortName.substring(0, 2)}
                          </Avatar>
                        </Table.Td>
                        <Table.Td>
                          {team.primaryColor ? (
                            <ColorSwatch color={team.primaryColor} size={20} />
                          ) : (
                            <Text c="dimmed" size="sm">
                              -
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                          {team.name}
                        </Table.Td>
                        <Table.Td>{gameCount}</Table.Td>
                        <Table.Td>
                          {groupNames.length > 0 ? (
                            groupNames.join(", ")
                          ) : (
                            <Text c="dimmed" size="sm">
                              -
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>

        {/* Section: Gruppen */}
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Title order={3}>Gruppen</Title>
            <Button
              onClick={() => {
                setCreateModalOpen(true);
              }}
            >
              Neue Gruppe erstellen
            </Button>
          </Group>

          {competition.data.groups.length === 0 ? (
            <Text c="dimmed">Noch keine Gruppen vorhanden</Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 4 }} spacing="md">
              {competition.data.groups.map((group) => (
                <GroupCard
                  key={group.id}
                  group={group}
                  enrolledTeamIds={competition.data.teamIds}
                  competitionId={competitionId}
                  onAddTeamClick={setAddTeamModalGroupId}
                  onResolveSlotsClick={setResolveSlotsGroupId}
                />
              ))}
            </SimpleGrid>
          )}
        </Stack>

        {/* Section: Spieltage */}
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Title order={3}>Spieltage</Title>
            <Button
              onClick={() => {
                setCreateMatchdayModalOpen(true);
              }}
            >
              Neuer Spieltag
            </Button>
          </Group>
          {competition.data.matchdays.length === 0 ? (
            <Text c="dimmed">Noch keine Spieltage vorhanden</Text>
          ) : (
            <Table.ScrollContainer minWidth={400}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Anzahl Spiele</Table.Th>
                    <Table.Th>Aktionen</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {competition.data.matchdays.map((matchday) => {
                    const matchdayGames = games.data.games.filter(
                      (g) => g.matchdayId === matchday.id,
                    );
                    const gamesNeedingTeams = matchdayGames.filter(
                      (g) =>
                        !g.actualTeams.homeTeamId || !g.actualTeams.guestTeamId,
                    );
                    return (
                      <Table.Tr key={matchday.id}>
                        <Table.Td>{matchday.name}</Table.Td>
                        <Table.Td>{matchdayGames.length}</Table.Td>
                        <Table.Td>
                          <Button
                            variant="light"
                            size="xs"
                            onClick={() => {
                              void navigate(
                                `/control/competitions/${competitionId}/matchdays/${matchday.id}/teams`,
                              );
                            }}
                            disabled={
                              matchdayGames.length === 0 ||
                              gamesNeedingTeams.length === 0
                            }
                          >
                            Teams festlegen
                            {gamesNeedingTeams.length > 0 && (
                              <Badge
                                size="xs"
                                color="orange"
                                variant="filled"
                                ml={6}
                              >
                                {gamesNeedingTeams.length}
                              </Badge>
                            )}
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Stack>

      <CreateGroupModal
        opened={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
        }}
        competitionId={competitionId}
        enrolledTeams={enrolledTeams}
      />

      <AddTeamToGroupModal
        groupId={addTeamModalGroupId}
        onClose={() => {
          setAddTeamModalGroupId(null);
        }}
        competitionId={competitionId}
        availableTeams={
          addTeamModalGroupId
            ? getAvailableTeamsForGroup(addTeamModalGroupId)
            : []
        }
      />

      {resolveSlotsGroupId && (
        <ResolveSlotsModal
          competitionId={competitionId}
          groupId={resolveSlotsGroupId}
          group={competition.data.groups.find(
            (g) => g.id === resolveSlotsGroupId,
          )}
          enrolledTeams={enrolledTeams}
          onClose={() => {
            setResolveSlotsGroupId(null);
          }}
          onSuccess={() => {
            void queryClient.invalidateQueries({
              queryKey: ["competition", { competitionId }],
            });
            setResolveSlotsGroupId(null);
          }}
        />
      )}

      <EnrollTeamsModal
        opened={enrollModalOpen}
        onClose={() => {
          setEnrollModalOpen(false);
        }}
        competitionId={competitionId}
        availableTeams={availableTeamsToEnroll}
      />

      <CreateMatchdayModal
        opened={createMatchdayModalOpen}
        onClose={() => {
          setCreateMatchdayModalOpen(false);
        }}
        competitionId={competitionId}
      />
    </Page>
  );
};
